import { Component, EventEmitter, OnInit, Output , ChangeDetectorRef } from '@angular/core';
import { WizardStateService } from '../../../services/wizard-state.service';
import { take } from 'rxjs/operators';

type ResourceKey = 'farmed' | 'natural' | 'bonus' | 'mineral' | 'special';

interface ResourceDescriptions {
  [key: number]: string;
}

const RESOURCE_DESCRIPTIONS: ResourceDescriptions = {
  1: "Extremely Rare - Resources are nearly nonexistent. Only isolated locations contain them.",
  2: "Very Rare - Resources appear in a few scattered locations and are difficult to obtain.",
  3: "Rare - Resources exist but are limited to specific regions or conditions.",
  4: "Uncommon - Resources appear in several areas but are not widely available.",
  5: "Balanced / Typical - Resources are distributed in a normal and balanced manner across the map.",
  6: "Moderately Common - Resources are relatively easy to find and support regular use or trade.",
  7: "Common - Resources are widely available across many regions.",
  8: "Abundant - Resources are widespread and produced or found in high quantities.",
  9: "Highly Abundant - Resources are extremely common and play a major role in regional economies.",
  10: "Dominant Resource - Resources are exceptionally abundant and define the map's economy or environment."
};

@Component({
    selector: 'app-resource-config-step',
    templateUrl: './resource-config-step.component.html',
    styleUrls: ['./resource-config-step.component.scss'],
    standalone: false
})
export class ResourceConfigStepComponent implements OnInit {
  @Output() next = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  currentPart = 1; // 1 or 2
  mapName = '';
  
  farmed = 5;
  natural = 5;
  bonus = 5;
  mineral = 5;
  special = 5;

  descriptions = RESOURCE_DESCRIPTIONS;

  /** The two pages of sliders (template loops over these). */
  readonly pages: Record<number, { key: ResourceKey; name: string; text: string; image: string }[]> = {
    1: [
      { key: 'farmed', name: 'Farmed', text: 'Crops and livestock from farming and managed food production.', image: 'assets/images/resources/image-1.png' },
      { key: 'natural', name: 'Natural', text: 'Wild resources taken straight from the land: timber, game and more.', image: 'assets/images/resources/image-2.png' },
    ],
    2: [
      { key: 'bonus', name: 'Bonus', text: 'Valuable regional goods that give a trade or economic edge, tied to climate, culture or geography.', image: 'assets/images/resources/image-4.png' },
      { key: 'mineral', name: 'Mineral', text: 'Metals, stone and rare ores mined for industry, crafting and war.', image: 'assets/images/resources/image-3.png' },
      { key: 'special', name: 'Special', text: "Rare, extraordinary resources that shape a region's identity, power or strategic value.", image: 'assets/images/resources/image-5.png' },
    ],
  };

  value(key: ResourceKey): number {
    return this[key];
  }

  /** "Balanced / Typical - Resources are…" → level name and the sentence. */
  level(val: number): { name: string; text: string } {
    const [name, ...rest] = (this.descriptions[val] || '').split(' - ');
    return { name, text: rest.join(' - ') };
  }

  constructor(private stateService: WizardStateService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Load existing state if available
    this.stateService.state.pipe(take(1)).subscribe(state => {
      if (state.mapName) {
         this.mapName = state.mapName;
      }
      if (state.resourceConfig) {
        this.farmed = state.resourceConfig.farmed;
        this.natural = state.resourceConfig.natural;
        this.bonus = state.resourceConfig.bonus;
        this.mineral = state.resourceConfig.mineral;
        this.special = state.resourceConfig.special;
      }
      this.cdr.detectChanges();
    });
  }

  getDescription(val: number): string {
    return this.descriptions[val];
  }

  onSliderChange(resource: ResourceKey, event: Event): void {
    const target = event.target as HTMLInputElement;
    this[resource] = parseInt(target.value, 10);
  }

  onNext(): void {
    if (this.currentPart === 1) {
      this.currentPart = 2;
      this.scrollToTop();
    } else {
      // Save state and emit next
      this.stateService.updateState({
        resourceConfig: {
          farmed: this.farmed,
          natural: this.natural,
          bonus: this.bonus,
          mineral: this.mineral,
          special: this.special
        }
      });
      this.next.emit();
    }
  }

  onBack(): void {
    if (this.currentPart === 2) {
      this.currentPart = 1;
      this.scrollToTop();
    } else {
      this.back.emit();
    }
  }

  private scrollToTop(): void {
    setTimeout(() => {
      const container = document.querySelector('.world-builder-container');
      if (container) {
        container.scrollTop = 0;
      }
    }, 20);
  }
}
